// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

// Runtime Node-global polyfills the Web3Auth SDK and the Ledger device
// stack (ledger-bitcoin's AppClient + @ledgerhq/hw-transport-webhid) expect
// (Buffer + a minimal process; `global` they reference only behind typeof
// guards).
//
// Front-loaded as the FIRST import of the entry (main.tsx) so the globals
// exist before any lazy chunk loads. This is deliberate and load-bearing:
// those deps touch bare-global `Buffer` at MODULE-EVAL time (e.g.
// ledger-bitcoin psbtv2.js `Buffer.from(...)` at module scope), and a
// side-effect import *inside* the lazy chunk does NOT guarantee ordering —
// ESM hoists a chunk's static imports above any inlined polyfill statement,
// so the dep can evaluate before the assignment runs and throw `Cannot read
// properties of undefined (reading 'from'/'alloc')`. The entry always fully
// evaluates before any runtime `import()`, so setting the global here makes
// it visible to every lazy chunk regardless of concatenation order. Only
// pulls the small `buffer` package, so the entry stays device-free.
// web3auth-login.ts and hardware-connect.ts also import this (idempotent —
// the `if (!g.Buffer)` guards make re-entry a no-op) as belt-and-suspenders
// for isolated imports.
//
// Mirrors user-app's web/spa/src/node-polyfills.ts per "code quality rules
// are copied, not shared" (renamed from web3auth-polyfills.ts here at HW.14
// when the hardware chunk became its second consumer, matching user-app).

import { Buffer as NodeBuffer } from 'buffer';

const g = globalThis as unknown as Record<string, unknown>;

if (!g.Buffer) g.Buffer = NodeBuffer;
if (!g.global) g.global = globalThis;
if (!g.process) {
  g.process = {
    env: {} as Record<string, string | undefined>,
    // `browser`/`version`/`versions`/`platform` are load-bearing, not
    // decoration: the device chunk's browserify crypto stack pulls
    // `readable-stream` (via hash-base → create-hash → bs58check → wif →
    // bip32), whose `_stream_writable.js` runs at MODULE-EVAL:
    //   `!process.browser && [...].indexOf(process.version.slice(0, 5)) > -1`.
    // With no `browser` and no `version`, `!process.browser` is truthy and
    // `process.version.slice(...)` throws `Cannot read properties of undefined
    // (reading 'slice')` the instant the chunk loads. `browser: true`
    // short-circuits the `&&`; `version`/`versions`/`platform` cover libs that
    // read them unconditionally. (HW.14 follow-up, COB-334 — surfaced only once
    // the vite-plugin-node-polyfills fix let the chunk eval past the earlier
    // `Buffer.alloc` runtime crash. The plugin's `globals` process injection is
    // off by design, so this manual global is the sole bare-global `process`.)
    browser: true,
    version: '',
    versions: {} as Record<string, string | undefined>,
    platform: 'browser',
    nextTick: (cb: () => void) => queueMicrotask(cb),
  };
}
